/**
 * Microsoft Authentication Library (MSAL) Configuration
 * Configures Azure AD authentication for Endeavor Communities
 */

import { Configuration, LogLevel } from '@azure/msal-browser';

/**
 * Check if MSAL is properly configured
 */
export function isMsalConfigured(): boolean {
  const clientId = import.meta.env.VITE_AZURE_CLIENT_ID;
  const tenantId = import.meta.env.VITE_AZURE_TENANT_ID;

  const isConfigured = !!(clientId && tenantId);

  if (!isConfigured) {
    console.warn('[msalConfig] Missing Azure AD configuration:', {
      hasClientId: !!clientId,
      hasTenantId: !!tenantId,
    });
  }

  return isConfigured;
}

/**
 * Get MSAL configuration
 * Created as a function to ensure environment variables are read at runtime
 */
export function getMsalConfig(): Configuration {
  const clientId = import.meta.env.VITE_AZURE_CLIENT_ID;
  const tenantId = import.meta.env.VITE_AZURE_TENANT_ID;

  if (!clientId) {
    console.error('[msalConfig] VITE_AZURE_CLIENT_ID is not set!');
  }
  if (!tenantId) {
    console.error('[msalConfig] VITE_AZURE_TENANT_ID is not set!');
  }

  return {
    auth: {
      // Application (client) ID from Azure AD app registration
      clientId: clientId || '',

      // Azure AD tenant-specific authority
      authority: `https://login.microsoftonline.com/${tenantId || 'common'}`,

      // Redirect URI after authentication
      redirectUri: `${window.location.origin}/auth/callback`,

      // Where to redirect after logout
      postLogoutRedirectUri: window.location.origin,

      // Navigate to the original request URL after login
      navigateToLoginRequestUrl: true,
    },
    cache: {
      // Store tokens in localStorage for persistence across tabs/sessions
      cacheLocation: 'localStorage',

      // Set to true if you need IE11 support
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          // Don't log PII (personally identifiable information)
          if (containsPii) return;

          switch (level) {
            case LogLevel.Error:
              console.error('[MSAL]', message);
              break;
            case LogLevel.Warning:
              console.warn('[MSAL]', message);
              break;
            case LogLevel.Info:
              // Only log info in development
              if (import.meta.env.DEV) {
                console.info('[MSAL]', message);
              }
              break;
          }
        },
        logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
      },
    },
  };
}

// For backward compatibility - lazy initialization
let _msalConfig: Configuration | null = null;
export const msalConfig: Configuration = new Proxy({} as Configuration, {
  get(_, prop) {
    if (!_msalConfig) {
      _msalConfig = getMsalConfig();
    }
    return (_msalConfig as Record<string | symbol, unknown>)[prop];
  },
});

// Login request configuration
//
// Sites.ReadWrite.All is requested at sign-in so SharePoint access is consented
// up front. Otherwise the app has to acquire it incrementally later via a silent
// hidden iframe, which Safari's tracking prevention blocks (AADSTS160021) — and
// that's what breaks AI Import / OM uploads in Safari. With the scope in the
// initial token, acquireTokenSilent uses the cached refresh token directly (no
// iframe), so it works across browsers.
export const loginRequest = {
  scopes: ['User.Read', 'email', 'profile', 'openid', 'Sites.ReadWrite.All'],
};

/**
 * Scopes for accessing Microsoft Graph API
 */
export const graphScopes = {
  // User profile
  profile: ['User.Read'],

  // File access (OneDrive)
  files: ['Files.ReadWrite.All'],

  // SharePoint access
  sharePoint: ['Sites.ReadWrite.All'],

  // Email access (read)
  mail: ['Mail.Read'],

  // Email access (send)
  mailSend: ['Mail.Send'],

  // Email access (read and send)
  mailReadWrite: ['Mail.Read', 'Mail.Send'],

  // Calendar access (optional)
  calendar: ['Calendars.Read'],
};

/**
 * Microsoft Graph API endpoints
 */
export const graphEndpoints = {
  me: 'https://graph.microsoft.com/v1.0/me',
  myPhoto: 'https://graph.microsoft.com/v1.0/me/photo/$value',
  myDrive: 'https://graph.microsoft.com/v1.0/me/drive',
  myDriveRoot: 'https://graph.microsoft.com/v1.0/me/drive/root/children',
  sendMail: 'https://graph.microsoft.com/v1.0/me/sendMail',
  messages: 'https://graph.microsoft.com/v1.0/me/messages',
};
